import { useState } from 'react';

interface EmailVerificationData {
  accessToken: string;
  newEmail: string;
}

interface UseAccountModalsReturn {
  emailChange: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  emailVerification: {
    isOpen: boolean;
    data: EmailVerificationData | null;
    open: (accessToken: string, newEmail: string) => void;
    close: () => void;
  };
  deleteAccount: {
    isOpen: boolean;
    open: () => void;
    close: () => void;
  };
  handleEmailChangeInitiated: (accessToken: string, newEmail: string) => void;
  handleEmailVerified: () => void;
  handleAccountDeleted: () => void;
}

/**
 * Hook to manage all account-related modal states.
 * Platform-agnostic: Pure state management.
 *
 * @returns Modal state and control functions
 */
export const useAccountModals = (): UseAccountModalsReturn => {
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<EmailVerificationData | null>(null);

  const handleEmailChangeInitiated = (accessToken: string, newEmail: string) => {
    setPendingEmailChange({ accessToken, newEmail });
    setShowEmailChangeModal(false);
    setShowEmailVerificationModal(true);
  };

  const handleEmailVerified = () => {
    setPendingEmailChange(null);
    setShowEmailVerificationModal(false);
    // Reload page to refresh profile data
    window.location.reload();
  };

  const handleAccountDeleted = () => {
    // Navigate to sign out
    window.location.href = '/signout';
  };

  return {
    emailChange: {
      isOpen: showEmailChangeModal,
      open: () => setShowEmailChangeModal(true),
      close: () => setShowEmailChangeModal(false),
    },
    emailVerification: {
      isOpen: showEmailVerificationModal,
      data: pendingEmailChange,
      open: (accessToken: string, newEmail: string) => {
        setPendingEmailChange({ accessToken, newEmail });
        setShowEmailVerificationModal(true);
      },
      close: () => {
        setShowEmailVerificationModal(false);
        setPendingEmailChange(null);
      },
    },
    deleteAccount: {
      isOpen: showDeleteAccountModal,
      open: () => setShowDeleteAccountModal(true),
      close: () => setShowDeleteAccountModal(false),
    },
    handleEmailChangeInitiated,
    handleEmailVerified,
    handleAccountDeleted,
  };
};
